import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Separator } from "@/components/ui/separator";
import { SITE_INFO } from "@/lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

export default function PoliciesPage() {
  return (
    <>
      <Helmet>
        <title>Policies, Terms & Waivers | Texoma Resorts</title>
        <meta name="description" content="Review the policies, terms of service, cancellation policy, and liability waivers for Texoma Resorts near Lake Texoma." />
      </Helmet>

      <section className="pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-accent mb-3">Legal</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-foreground tracking-tight">
              Policies & Terms
            </h1>
          </motion.div>
        </div>
      </section>

      <section data-testid="policies-content" className="pb-16 md:pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="space-y-10">
            <div>
              <h2 className="font-heading text-2xl font-normal text-foreground mb-4">Cancellation Policy</h2>
              <div className="font-body text-sm text-muted-foreground leading-relaxed space-y-3">
                <p>Cancellations made 7 or more days before the scheduled check-in date will receive a full refund.</p>
                <p>Cancellations made within 7 days of check-in are subject to a charge equivalent to one night&apos;s stay.</p>
                <p>No-shows will be charged the full reservation amount.</p>
                <p>Modifications to reservations are subject to availability and may result in price adjustments.</p>
              </div>
            </div>

            <Separator />

            <div>
              <h2 className="font-heading text-2xl font-normal text-foreground mb-4">Check-In & Check-Out</h2>
              <div className="font-body text-sm text-muted-foreground leading-relaxed space-y-3">
                <p>Check-in time: 3:00 PM</p>
                <p>Check-out time: 11:00 AM</p>
                <p>Early check-in and late check-out may be available upon request and are subject to availability.</p>
                <p>Self check-in instructions will be provided prior to arrival.</p>
              </div>
            </div>

            <Separator />

            <div>
              <h2 className="font-heading text-2xl font-normal text-foreground mb-4">Pet Policy</h2>
              <div className="font-body text-sm text-muted-foreground leading-relaxed space-y-3">
                <p>Texoma Resorts is pet-friendly. Pets are welcome in designated accommodations.</p>
                <p>Pets must be leashed at all times when outside of your accommodation.</p>
                <p>Owners are responsible for cleaning up after their pets.</p>
                <p>A pet fee may apply. Please notify us at the time of booking if you plan to bring a pet.</p>
              </div>
            </div>

            <Separator />

            <div>
              <h2 className="font-heading text-2xl font-normal text-foreground mb-4">Quiet Hours & Campus Rules</h2>
              <div className="font-body text-sm text-muted-foreground leading-relaxed space-y-3">
                <p>Quiet hours are observed from 10:00 PM to 7:00 AM.</p>
                <p>Fires are permitted only in designated fire pits.</p>
                <p>No glass containers at campsites or common areas.</p>
                <p>All guests must follow Leave No Trace principles.</p>
                <p>Smoking is permitted only in designated areas.</p>
              </div>
            </div>

            <Separator />

            <div>
              <h2 className="font-heading text-2xl font-normal text-foreground mb-4">Liability Waiver</h2>
              <div className="font-body text-sm text-muted-foreground leading-relaxed space-y-3">
                <p>By booking and staying at Texoma Resorts, guests acknowledge and accept the inherent risks associated with outdoor activities, including but not limited to camping, ATV rides, boating, and hiking.</p>
                <p>Texoma Resorts and Serenity Village are not liable for personal injury, property damage, or loss resulting from participation in resort activities.</p>
                <p>Guests participating in adventure experiences (ATV rides, speed boat tours) may be required to sign an additional activity-specific waiver prior to participation.</p>
                <p>Parents or guardians are responsible for the supervision of children at all times.</p>
              </div>
            </div>

            <Separator />

            <div>
              <h2 className="font-heading text-2xl font-normal text-foreground mb-4">Contact</h2>
              <div className="font-body text-sm text-muted-foreground leading-relaxed space-y-2">
                <p>For questions about our policies, please contact us:</p>
                <p>Email: <a href={`mailto:${SITE_INFO.email}`} className="text-primary hover:underline">{SITE_INFO.email}</a></p>
                <p>Phone: <a href={`tel:${SITE_INFO.phoneRaw}`} className="text-primary hover:underline">{SITE_INFO.phone}</a></p>
                <p>Address: {SITE_INFO.address}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
